import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, Select, SearchableSelect, Modal, ModalFooter, ErrorSummary } from '@/components/common';
import api from '@/services/api';
import { applyServerErrors } from '@/utils/formErrors';

export interface FormFieldConfig {
  name: string;
  label: string;
  type: 'text' | 'select' | 'number' | 'date' | 'boolean';
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  apiEndpoint?: string;
  searchable?: boolean;
}

interface AdminCrudFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  formFields: FormFieldConfig[];
  initialData?: Record<string, unknown> | null;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  title: string;
}

function FkSelect({
  field,
  value,
  onChange,
  error,
}: {
  field: FormFieldConfig;
  value: string;
  onChange: (value: string) => void;
  error?: string;
}) {
  // A picker must offer every row, and these endpoints paginate at 20 by
  // default. Production has 30 insurers, so the Insurer picker was quietly
  // offering the first 20 and the rest could not be chosen at all -- and
  // because `searchable` filters client-side over what was already fetched,
  // typing a missing insurer's name found nothing and read as "we don't have
  // that insurer". 100 is this API's max_page_size, the largest single page it
  // will honour. Beyond that a picker needs server-side search rather than a
  // bigger page, so the assumption is worth stating: FK pickers here are for
  // reference tables, not for unbounded ones like contacts or policies.
  const endpoint = field.apiEndpoint
    ? field.apiEndpoint.includes('page_size=')
      ? field.apiEndpoint
      : `${field.apiEndpoint}${field.apiEndpoint.includes('?') ? '&' : '?'}page_size=100`
    : undefined;

  const { data, isLoading } = useQuery({
    // The endpoint is part of the key, so a caller overriding page_size gets
    // its own cache entry rather than another field's truncated list.
    queryKey: ['fk-options', endpoint],
    queryFn: async () => {
      const response = await api.get(endpoint!);
      // Handle both paginated { results: [...] } and plain array responses.
      // Some endpoints (the DRF router root, or any future endpoint that
      // doesn't paginate the way we expect) return a plain object with
      // neither shape -- e.g. `/insurers/` itself resolves to
      // `{ insurers: 'http://.../insurers/insurers/', employees: '...', ... }`.
      // That has no `.results` and is not an array, so it must never reach
      // `.map` below: this used to fall through to the raw object and throw
      // `TypeError: data.map is not a function` during render, taking the
      // whole page down.
      return response.data;
    },
    enabled: !!field.apiEndpoint,
    staleTime: 5 * 60 * 1000,
  });

  const rows: Array<Record<string, unknown>> = Array.isArray(data)
    ? data
    : (data?.results ?? []);

  const options: Array<{ value: string; label: string }> = rows.map((item) => ({
    value: String(item.id),
    // `name` isn't universal -- e.g. `/users/` rows carry `email` and
    // `full_name` but no `name`, which used to render every option blank.
    // Fall back through the fields most endpoints actually have, and worst
    // case fall back to the id so the option is never an empty string.
    label: String(item.name ?? item.full_name ?? item.email ?? item.code ?? item.id),
  }));

  if (field.searchable) {
    return (
      <SearchableSelect
        label={field.label}
        options={options}
        value={value || ''}
        onChange={onChange}
        required={field.required}
        error={error}
        placeholder={isLoading ? 'Loading...' : `Select ${field.label.toLowerCase()}`}
      />
    );
  }

  return (
    <Select
      label={field.label}
      options={options}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      required={field.required}
      error={error}
      placeholder={isLoading ? 'Loading...' : `Select ${field.label.toLowerCase()}`}
    />
  );
}

export function AdminCrudFormModal({
  isOpen,
  onClose,
  formFields,
  initialData,
  onSubmit,
  title,
}: AdminCrudFormModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: initialData || {},
  });

  // Server-side validation messages from the last rejected submit, shown in
  // the banner below. `applyServerErrors` (the same helper UserFormPage,
  // CustomerFormPage, PolicyCreatePage etc. already use) also calls
  // `setError` for every field-keyed message so it shows under its own
  // input -- but the banner is what guarantees a message for a field this
  // config doesn't render an input for (or a non-field rejection) is never
  // silently dropped. That is exactly the regression Task 1's own review
  // caught: an error keyed to a field with no `error=` consumer went nowhere.
  const [serverMessages, setServerMessages] = useState<string[]>([]);

  // Reset form when modal opens/closes or initialData changes
  useEffect(() => {
    if (isOpen) {
      reset(initialData || {});
      setServerMessages([]);
    }
  }, [isOpen, initialData, reset]);

  // Names react-hook-form actually tracks via `register()` -- text/number/
  // date/boolean inputs and the plain (non-searchable, non-FK) `Select`
  // below. FK selects (`apiEndpoint`) and static searchable selects are
  // controlled through `watch`/`setValue` only and never call `register()`,
  // so RHF's `_fields` has no entry for them; `handleSubmit`'s own built-in
  // validation cycle only clears entries in `_formState.errors` for names
  // present in `_fields`, so a `setError` on any other name is never cleared
  // by resubmission. Used below to keep `applyServerErrors` from calling
  // `setError` on a name RHF can never un-set on its own (B3, 2026-08-06
  // whole-branch review) -- the message still reaches the user via the
  // ErrorSummary banner regardless, same as any field this config doesn't
  // render an input for at all.
  const registeredFieldNames = new Set(
    formFields.filter((field) => !(field.type === 'select' && (field.apiEndpoint || field.searchable))).map((field) => field.name)
  );

  const handleFormSubmit = async (data: Record<string, unknown>) => {
    setServerMessages([]);
    // `searchable` selects (FK or static) render `SearchableSelect`, a
    // button with no native form participation -- unlike the plain `Select`
    // branch below, which spreads a real `required` onto a native <select>
    // the browser blocks submission on by itself. Their value also flows
    // through `watch`/`setValue` rather than `register`, so react-hook-form
    // has no validation rule for them either: `handleSubmit` above has
    // already run by the time we get here regardless of whether they're
    // empty. Without this check, an empty required searchable field would
    // reach the API and come back as an opaque toast instead of being
    // caught here -- the same gap Task 6 found and fixed page-by-page
    // (5545ad1); fixed once here since every AdminCrudPage config shares
    // this modal.
    const missingRequired = formFields.filter(
      (field) => field.type === 'select' && field.searchable && field.required && !data[field.name]
    );
    if (missingRequired.length > 0) {
      missingRequired.forEach((field) => {
        setError(field.name, { type: 'required', message: `${field.label} is required` });
      });
      return;
    }
    try {
      await onSubmit(data);
    } catch (error) {
      setServerMessages(
        applyServerErrors(error, (name, err) => {
          // Only forward to react-hook-form's setError for a name it can
          // actually clear again later (see registeredFieldNames above) --
          // otherwise Save silently no-ops on every submit after this one,
          // for the rest of the modal's session (B3). applyServerErrors
          // still returns every message for the banner unconditionally, so
          // nothing here is dropped -- it just doesn't get an inline
          // per-field binding it could never recover from.
          if (registeredFieldNames.has(name)) {
            setError(name, err);
          }
        })
      );
    }
  };

  const renderField = (field: FormFieldConfig) => {
    const fieldError = errors[field.name]?.message as string | undefined;

    switch (field.type) {
      case 'select': {
        // FK select with apiEndpoint
        if (field.apiEndpoint) {
          return (
            <FkSelect
              key={field.name}
              field={field}
              value={(watch(field.name) as string) || ''}
              onChange={(val) => {
                setValue(field.name, val, { shouldValidate: true });
                // This field is never `register()`'d (see the `missingRequired`
                // comment in handleFormSubmit below), so `shouldValidate` above
                // is a no-op for it -- react-hook-form's built-in validation
                // cycle only ever touches registered fields, and never clears
                // an error `setError` set manually on one that isn't. Without
                // this, `handleFormSubmit`'s own required check would set an
                // error once and it would never clear, which means
                // `handleSubmit` stops invoking `handleFormSubmit` at all (its
                // own `isEmptyObject(errors)` gate never passes again) --
                // silently bricking the form for the rest of the session, even
                // after the user picks a valid value. Empirically verified
                // against react-hook-form 7.71.1 before landing this.
                clearErrors(field.name);
              }}
              error={fieldError}
            />
          );
        }
        // Static options select. `searchable` needs a controlled value/onChange
        // (like the FK select above), so it can't stay on `register`'s
        // uncontrolled spread the way the plain `Select` below does.
        if (field.searchable) {
          return (
            <SearchableSelect
              key={field.name}
              label={field.label}
              options={field.options || []}
              value={(watch(field.name) as string) || ''}
              onChange={(val) => {
                setValue(field.name, val, { shouldValidate: true });
                // Same unregistered-field gap as FkSelect above.
                clearErrors(field.name);
              }}
              required={field.required}
              error={fieldError}
              placeholder={`Select ${field.label.toLowerCase()}`}
            />
          );
        }
        return (
          <Select
            key={field.name}
            label={field.label}
            options={field.options || []}
            required={field.required}
            error={fieldError}
            placeholder={`Select ${field.label.toLowerCase()}`}
            {...register(field.name, {
              required: field.required ? `${field.label} is required` : false,
            })}
          />
        );
      }

      case 'boolean':
        return (
          <div key={field.name}>
            <div className="flex items-center gap-2 pt-6">
              <input
                type="checkbox"
                id={field.name}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                {...register(field.name)}
              />
              <label htmlFor={field.name} className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {field.label}
              </label>
            </div>
            {fieldError && <p className="mt-1 text-sm text-red-600">{fieldError}</p>}
          </div>
        );

      case 'number':
        return (
          <Input
            key={field.name}
            type="number"
            label={field.label}
            required={field.required}
            error={fieldError}
            {...register(field.name, {
              required: field.required ? `${field.label} is required` : false,
              valueAsNumber: true,
            })}
          />
        );

      case 'date':
        return (
          <Input
            key={field.name}
            type="date"
            label={field.label}
            required={field.required}
            error={fieldError}
            {...register(field.name, {
              required: field.required ? `${field.label} is required` : false,
            })}
          />
        );

      case 'text':
      default:
        return (
          <Input
            key={field.name}
            label={field.label}
            required={field.required}
            error={fieldError}
            {...register(field.name, {
              required: field.required ? `${field.label} is required` : false,
            })}
          />
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <ErrorSummary messages={serverMessages} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {formFields.map(renderField)}
        </div>
        <ModalFooter>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" isLoading={isSubmitting}>
            {initialData ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
